/**
 * useExperienceWizard Hook
 *
 * Manages state and navigation for the 5-stage Experience Creation Wizard.
 * Implements wizard state machine with validation and navigation logic.
 *
 * Epic: MVP 1.27 - Experience Creation Wizard
 * Story: SCRUM-184 (US-1.27.1-03: Stage Navigation)
 * Location: apps/web/src/hooks/experiences/useExperienceWizard.js
 */

import { useState, useCallback } from 'react';

/**
 * Initial form data structure
 */
const getInitialFormData = () => ({
  // Stage 1: Basics
  experienceName: '',
  categoryL1Id: null,
  categoryL2Id: null,
  categoryL3Id: null,
  description: '',
  highlights: [],
  difficultyLevel: '',

  // Stage 2: Logistics
  meetingPointAddress: '',
  meetingLat: null,
  meetingLng: null,
  meetingState: '',
  durationType: 'hours',
  durationHours: 4,
  durationDays: null,
  prepDays: 0,
  cleanupDays: 0,
  startDayRestrictions: {},
  minGroupSize: 1,
  maxGroupSize: 4,
  whatsIncluded: [],
  whatToBring: [],

  // Stage 2: Key Locations (MVP 1.20.6-S)
  keyLocations: {
    meeting_point: {},
    waypoints: [],
    route_overview: '',
    route_stats: null,
    elevation_data: null,
    gpx_source: null
  },

  // Stage 3: Pricing
  pricingModel: 'per_person', // 'per_person' | 'flat_group' | 'base_plus_per' | 'base_covers_n'
  basePriceCents: 0,
  perPersonPriceCents: 0,
  includedCount: 2,
  bookingLeadTime: 24, // hours

  // Stage 4: Media
  coverPhoto: null,
  galleryPhotos: [],

  // Metadata
  isDraft: true,
  lastSavedAt: null
});

/**
 * useExperienceWizard Hook
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.initialData - Pre-populate wizard with existing data (for editing)
 * @param {Function} options.onStageChange - Callback when stage changes: (newStage, oldStage) => void
 * @param {Function} options.onComplete - Callback when wizard is completed: (formData) => void
 * @returns {Object} Wizard state and navigation functions
 */
export default function useExperienceWizard({
  initialData = null,
  onStageChange = null,
  onComplete = null
} = {}) {
  // State — when editing (initialData provided), all stages are navigable
  const [currentStage, setCurrentStage] = useState(1);
  const [completedStages, setCompletedStages] = useState(
    initialData ? [1, 2, 3, 4] : []
  );
  const [highestCompletedStage, setHighestCompletedStage] = useState(
    initialData ? 4 : 0
  );
  const [formData, setFormData] = useState(() => {
    return initialData ? { ...getInitialFormData(), ...initialData } : getInitialFormData();
  });

  /**
   * Update form data (merge updates)
   */
  const updateFormData = useCallback((updates) => {
    setFormData((prev) => ({
      ...prev,
      ...updates
    }));
  }, []);

  /**
   * Update a single form field
   */
  const updateField = useCallback((fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value
    }));
  }, []);

  /**
   * Check if user can navigate to a specific stage
   * Rules:
   * - Can always go to current stage
   * - Can go to any completed stage
   * - Can go to next stage if current is in completedStages
   */
  const canNavigateToStage = useCallback((targetStage) => {
    // Can't navigate beyond stage 5 or below stage 1
    if (targetStage < 1 || targetStage > 5) {return false;}

    // Can always navigate to current stage
    if (targetStage === currentStage) {return true;}

    // Can navigate to any stage up to highestCompletedStage + 1
    return targetStage <= highestCompletedStage + 1;
  }, [currentStage, highestCompletedStage]);

  /**
   * Navigate to a specific stage
   */
  const goToStage = useCallback((targetStage) => {
    if (!canNavigateToStage(targetStage)) {
      console.warn(`Cannot navigate to stage ${targetStage}`);
      return false;
    }

    const oldStage = currentStage;
    setCurrentStage(targetStage);

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Call onStageChange callback
    if (onStageChange) {
      onStageChange(targetStage, oldStage);
    }

    return true;
  }, [currentStage, canNavigateToStage, onStageChange]);

  /**
   * Mark current stage as completed and move to next
   */
  const completeCurrentStage = useCallback(() => {
    // Add current stage to completed stages
    setCompletedStages((prev) => {
      if (!prev.includes(currentStage)) {
        return [...prev, currentStage];
      }
      return prev;
    });

    // Update highest completed stage
    setHighestCompletedStage((prev) => Math.max(prev, currentStage));

    // If not on last stage, move to next
    if (currentStage < 5) {
      const nextStage = currentStage + 1;

      // Directly set the stage without checking canNavigateToStage
      // because we just completed the current stage
      setCurrentStage(nextStage);

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Call onStageChange callback
      if (onStageChange) {
        onStageChange(nextStage, currentStage);
      }
    } else {
      // Last stage completed - call onComplete
      if (onComplete) {
        onComplete(formData);
      }
    }
  }, [currentStage, formData, onStageChange, onComplete]);

  /**
   * Go to next stage (without marking current as complete)
   */
  const goToNextStage = useCallback(() => {
    if (currentStage < 5) {
      goToStage(currentStage + 1);
    }
  }, [currentStage, goToStage]);

  /**
   * Go to previous stage
   */
  const goToPreviousStage = useCallback(() => {
    if (currentStage > 1) {
      goToStage(currentStage - 1);
    }
  }, [currentStage, goToStage]);

  /**
   * Reset wizard to initial state
   */
  const resetWizard = useCallback(() => {
    setCurrentStage(1);
    setCompletedStages([]);
    setHighestCompletedStage(0);
    setFormData(getInitialFormData());
  }, []);

  /**
   * Get stage validation status
   * Returns object with validation errors for current stage
   */
  const getStageValidation = useCallback((stageNumber) => {
    const errors = {};

    switch (stageNumber) {
      case 1: // Basics
        if (!formData.experienceName || formData.experienceName.length < 3) {
          errors.experienceName = 'Experience name must be at least 3 characters';
        }
        if (!formData.categoryL1Id) {
          errors.categoryL1Id = 'Please select a primary category';
        }
        if (!formData.categoryL2Id) {
          errors.categoryL2Id = 'Please select a secondary category';
        }
        if (!formData.description || formData.description.length < 50) {
          errors.description = 'Description must be at least 50 characters';
        }
        if (formData.highlights.length < 3) {
          errors.highlights = 'Please add at least 3 highlights';
        }
        break;

      case 2: // Logistics
        if (!formData.meetingPointAddress) {
          errors.meetingPointAddress = 'Please provide a meeting point';
        }
        if (formData.durationType === 'fixed_multi_day') {
          if (!formData.durationDays || formData.durationDays < 2 || formData.durationDays > 14) {
            errors.durationDays = 'Duration must be between 2 and 14 days';
          }
        } else {
          if (!formData.durationHours || formData.durationHours <= 0) {
            errors.durationHours = 'Duration must be greater than 0';
          }
        }
        if (formData.maxGroupSize < formData.minGroupSize) {
          errors.maxGroupSize = 'Max group size must be >= min group size';
        }
        break;

      case 3: // Pricing
        if (!formData.pricingModel) {
          errors.pricingModel = 'Please select a pricing model';
        }
        if (formData.pricingModel === 'per_person' && formData.perPersonPriceCents < 1000) {
          errors.perPersonPriceCents = 'Price must be at least $10';
        }
        if (formData.pricingModel === 'flat_group' && formData.basePriceCents < 1000) {
          errors.basePriceCents = 'Base price must be at least $10';
        }
        if (formData.pricingModel === 'base_plus_per') {
          if (formData.basePriceCents < 1000) {
            errors.basePriceCents = 'Base price must be at least $10';
          }
          if (formData.perPersonPriceCents < 1000) {
            errors.perPersonPriceCents = 'Per-person price must be at least $10';
          }
        }
        if (formData.pricingModel === 'base_covers_n') {
          if (formData.basePriceCents < 1000) {
            errors.basePriceCents = 'Base price must be at least $10';
          }
          if (formData.perPersonPriceCents < 1000) {
            errors.perPersonPriceCents = 'Per-person price must be at least $10';
          }
          if (!formData.includedCount || formData.includedCount < 1) {
            errors.includedCount = 'Included guest count must be at least 1';
          }
        }
        break;

      case 4: // Media
        if (!formData.coverPhoto) {
          errors.coverPhoto = 'Cover photo is required';
        }
        if (formData.galleryPhotos.length < 3) {
          errors.galleryPhotos = 'At least 3 gallery photos recommended';
        }
        break;

      case 5: // Review
        // All validation from previous stages
        break;

      default:
        break;
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }, [formData]);

  /**
   * Check if current stage is valid
   */
  const isCurrentStageValid = useCallback(() => {
    const { isValid } = getStageValidation(currentStage);
    return isValid;
  }, [currentStage, getStageValidation]);

  /**
   * Calculate overall completion percentage
   */
  const completionPercentage = Math.round((completedStages.length / 5) * 100);

  return {
    // State
    currentStage,
    completedStages,
    highestCompletedStage,
    formData,
    completionPercentage,

    // Navigation
    goToStage,
    goToNextStage,
    goToPreviousStage,
    completeCurrentStage,
    canNavigateToStage,

    // Form data management
    updateFormData,
    updateField,

    // Validation
    getStageValidation,
    isCurrentStageValid,

    // Utility
    resetWizard
  };
}

/**
 * Stage definitions (exported for use in components)
 */
export const WIZARD_STAGES = {
  BASICS: 1,
  LOGISTICS: 2,
  PRICING: 3,
  MEDIA: 4,
  REVIEW: 5
};

/**
 * Stage names (exported for display)
 */
export const STAGE_NAMES = {
  1: 'Basics',
  2: 'Logistics',
  3: 'Pricing',
  4: 'Media',
  5: 'Review'
};
